import { useEffect, useState } from "react";
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { useFetchNormativityAggregateReportMutation } from "../../../../api/rtk/base/slices/diagnosticsApiSlice";
import { calculatePercent } from "../../../../features/dataFormatFunctions/dataFormatter";
import { Loader } from "../../../loaders/Loader";
import { useFetchAxisLengthQuery } from "../../../../api/rtk/base/slices/pathsApiSlice";
import { useAppSelector } from "../../../../stateManagement/hooks/redux";

const WIDTH_FOR_3_GRAPHS_LABELS = 1900
const WIDTH_FOR_2_GRAPHS_LABELS = 1400

export default function NormativityPercentByYearsGraph() {
    const [graphData, setGraphData] = useState<any>([])
    const [graphIsShown, setGraphIsShown] = useState<boolean>(true)
    const [labelsAreHidden, setLabelsAreHidden] = useState<boolean>(true)
    const [normativityPercentIsFetching, setNormativityPercentIsFetching] = useState<boolean>(true)
    const [fetchNormativityAggregateReport] = useFetchNormativityAggregateReportMutation()
    const { data: axisLengthData, isFetching: axisLengthDataIsFetching } = useFetchAxisLengthQuery({})

    const sideBarState = useAppSelector(state => state.sidebar.sidebarIsShown)

    useEffect(() => {
        const numberOfGraphs = graphData.length
        const contentHeaderWidth: any = document.querySelector('.content-header-div')?.getBoundingClientRect().width;
        const labelsAreHidden = (
            numberOfGraphs > 3 ||
            numberOfGraphs === 3 && contentHeaderWidth < WIDTH_FOR_3_GRAPHS_LABELS ||
            numberOfGraphs === 2 && contentHeaderWidth < WIDTH_FOR_2_GRAPHS_LABELS
        )
        setLabelsAreHidden(labelsAreHidden)
    }, [sideBarState, graphData])

    useEffect(() => {
        if (axisLengthData?.Length) {
            getGraphData()
        }
    }, [axisLengthData])

    async function getGraphData() {
        const axisLength = axisLengthData?.Length
        setNormativityPercentIsFetching(true)
        const currentYear = new Date().getFullYear();
        const lastFiveYears = Array.from({ length: 5 }, (_, index) => currentYear - index).reverse();
        let data = []
        let hasAtLeastOneFilled = false
        for (const year of lastFiveYears) {
            const normativityAggregateReport: any = await fetchNormativityAggregateReport({ Year: year })
            const { Normative, NotNormative, Total } = normativityAggregateReport?.data ?? {}
            const totalPercent: any = calculatePercent(axisLength, Total)
            const normativePercent = calculatePercent(Total, Normative)
            const notNormativePercent = calculatePercent(Total, NotNormative)
            const relativeNormativePercent = totalPercent * normativePercent / 100
            const relativeNotNormativePercent = totalPercent * notNormativePercent / 100
            if (totalPercent > 0) {
                hasAtLeastOneFilled = true
                data.push({
                    totalPercent, normativePercent, relativeNormativePercent, notNormativePercent, relativeNotNormativePercent, year,
                    Total, Normative, NotNormative
                })
            }
        }
        setGraphIsShown(hasAtLeastOneFilled)
        setGraphData(data.slice(0, 2))
        setNormativityPercentIsFetching(false)
    }

    const graphTooltipTemplate: any = ({ payload }: any) => {
        const graphData = payload[0]?.payload
        const axisLength = axisLengthData?.Length?.toFixed(3)
        const { year, Total, Normative, NotNormative } = graphData ?? {}
        return (
            <div className="graph-tooltip">
                <p>Год: <strong>{year}</strong></p>
                {axisLength && <p>Общая протяжённость: <span id="axis-length">{axisLength} </span>км</p>}
                {Total && <p>Обследовано: <span id="total-value">{Total} </span>км</p>}
                {Normative && <p>В нормативе: <span id="normative-value">{Normative} </span>км</p>}
                {NotNormative && <p>Не в нормативе: <span id="not-normative-value">{NotNormative}</span> км</p>}
            </div>
        )
    }

    const percentLabelFormatter = (value: any) => `${value.toFixed(1)}%`
    const percentAxisTicks = [0, 20, 40, 60, 80, 100]
    const yearAxisTickFormatter = (value: any) => `конец ${value} г.`
    const legendFormatter = (value: any) => <span className="legend-text">{value}</span>

    return (
        <>
            {normativityPercentIsFetching
                ?
                <Loader />
                :
                <>
                    {graphIsShown
                        ?
                        <ResponsiveContainer width="100%" height={357}>
                            <BarChart
                                data={graphData}
                                layout="horizontal"
                                margin={{ right: 50, bottom: 15, top: 15 }}
                                className="normativity-percent-by-years-graph"
                            >
                                <defs>
                                    <linearGradient id="totalPercent">
                                        <stop offset="0%" stopColor="#3478c9" />
                                        <stop offset="100%" stopColor="#054a9b" />
                                    </linearGradient>
                                    <linearGradient id="normativePercent" >
                                        <stop offset="0%" stopColor="#34C9A4" />
                                        <stop offset="100%" stopColor="#059B76" />
                                    </linearGradient>

                                    <linearGradient id="notNormativePercent">
                                        <stop offset="0%" stopColor="#FF5349" />
                                        <stop offset="100%" stopColor="#b7410e" />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                                <YAxis type="number" axisLine={false} ticks={percentAxisTicks} tickLine={false} tick={{ fill: "#999999" }} />
                                <XAxis dataKey="year" type="category" tickFormatter={yearAxisTickFormatter} tick={{ fill: "black" }} />
                                <Legend formatter={legendFormatter} />
                                <Tooltip content={graphTooltipTemplate} />
                                <Bar
                                    dataKey="totalPercent"
                                    fill="url(#totalPercent)"
                                    name="Обследовано дорог"
                                    barSize={30}
                                    label={labelsAreHidden ? null : { fill: "black", position: 'left', fontSize: 14, formatter: percentLabelFormatter }}
                                    background={{ fill: '#cfcfcf' }}
                                />
                                <Bar
                                    dataKey="relativeNormativePercent"
                                    fill="url(#normativePercent)"
                                    stackId="normative"
                                    name="В нормативе"
                                    barSize={30}
                                    label={labelsAreHidden ? null : { dataKey: "normativePercent", fill: "black", position: 'right', fontSize: 14, formatter: percentLabelFormatter }}
                                />
                                <Bar
                                    dataKey="relativeNotNormativePercent"
                                    fill="url(#notNormativePercent)"
                                    stackId="normative"
                                    barSize={30}
                                    name="Не в нормативе"
                                    label={labelsAreHidden ? null : { dataKey: "notNormativePercent", fill: "black", position: 'right', fontSize: 14, formatter: percentLabelFormatter }}
                                />
                            </BarChart>
                        </ResponsiveContainer>
                        :
                        <div className="panel-no-content">
                            <span>Нет информации</span>
                        </div>
                    }
                </>
            }
        </>
    )
}